import type {
  AssetInstructionOut,
  DeviceAssetOut,
  GatewayAssetOut,
  GetAssetInstructionsAssetAssetIdInstructionsGetQueryParams,
} from "kubb";
import { useGetAssetInstructionsAssetAssetIdInstructionsGet } from "kubb";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import NewGetListPaginatedTable from "../../../../components/Pagination/GetListPaginatedTable";
import { TableHeader, TableRow } from "../../../../components/Theme/table";
import AssetInstructionRow from "./AssetInstructionRow";
import WorkflowInstructionRow from "./WorkflowInstructionRow";

interface Props {
  asset: GatewayAssetOut | DeviceAssetOut;
}

const AUTO_REFRESH_INTERVAL_MS = 15000; // 15 seconds
const ACTIVE_STATUSES = ["pending", "in_progress", "pending_retry"];

/**
 * Determines if an instruction is a workflow (has children).
 */
function isWorkflowInstruction(instruction: AssetInstructionOut): boolean {
  return (
    instruction.children !== null &&
    instruction.children !== undefined &&
    instruction.children.length > 0
  );
}

/**
 * Checks if any instruction (or its children) has an active status.
 */
function hasActiveInstructions(instructions: AssetInstructionOut[]): boolean {
  return instructions.some((instruction) => {
    // Check the instruction itself
    if (ACTIVE_STATUSES.includes(instruction.status)) {
      return true;
    }
    // Check children if present
    if (instruction.children?.length) {
      return instruction.children.some((child) =>
        ACTIVE_STATUSES.includes(child.status),
      );
    }
    return false;
  });
}

export default function AssetInstructionsTab(props: Props): React.ReactElement {
  const { asset } = props;
  const [lastUpdateRequested, setLastUpdateRequested] = useState<
    Date | undefined
  >();
  const [hasActiveItems, setHasActiveItems] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Handle data changes to determine if auto-refresh is needed
  const handleDataFetched = useCallback((data: AssetInstructionOut[]) => {
    setHasActiveItems(hasActiveInstructions(data));
  }, []);

  // Set up auto-refresh interval when there are active items
  useEffect(() => {
    if (hasActiveItems) {
      // Start the interval
      intervalRef.current = setInterval(() => {
        setLastUpdateRequested(new Date());
      }, AUTO_REFRESH_INTERVAL_MS);
    } else {
      // Clear the interval when no active items
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hasActiveItems]);

  const header = (
    <TableRow>
      <TableHeader>Instruction Type</TableHeader>
      <TableHeader>Asset</TableHeader>
      <TableHeader>Method</TableHeader>
      <TableHeader>Status</TableHeader>
      <TableHeader>Trigger</TableHeader>
      <TableHeader>User</TableHeader>
      <TableHeader>Attempts</TableHeader>
      <TableHeader>Created At</TableHeader>
      <TableHeader>Updated At</TableHeader>
    </TableRow>
  );

  const row = (instruction: AssetInstructionOut): React.ReactElement => {
    // Use WorkflowInstructionRow for instructions with children, otherwise use regular row
    if (isWorkflowInstruction(instruction)) {
      return (
        <WorkflowInstructionRow
          key={instruction.id}
          instruction={instruction}
          viewedAssetId={asset.asset_id}
          companyId={asset.company_id}
        />
      );
    }
    return (
      <AssetInstructionRow
        key={instruction.id}
        instruction={instruction}
        viewedAssetId={asset.asset_id}
        companyId={asset.company_id}
      />
    );
  };

  const noDataRow = (
    <TableRow>
      <td colSpan={9} className="py-4 text-center text-gray-500">
        No asset instructions have been sent to this asset yet.
      </td>
    </TableRow>
  );

  return (
    <NewGetListPaginatedTable
      params={
        {
          company_id: asset.company_id,
        } as GetAssetInstructionsAssetAssetIdInstructionsGetQueryParams
      }
      cursor={{ max_items: 25, direction: "desc" }}
      header={[header, 9]}
      row={row}
      useQuery={(params) =>
        useGetAssetInstructionsAssetAssetIdInstructionsGet(
          asset.asset_id,
          params,
        )
      }
      noDataRow={noDataRow}
      lastUpdateRequested={lastUpdateRequested}
      onDataFetched={handleDataFetched}
      showRefreshing={false}
    />
  );
}
